import { useState } from 'react';
import { filterDevicesByQuery } from '@/scheduling/lib/device-columns';
import { deviceCardItem } from '@/scheduling/lib/picker-cards';
import { PickerGrid } from '@/scheduling/components/shared/picker-grid';
import type { CalendarDevice } from '@/types/scheduling-api';

/**
 * DevicePickerGrid — the Book-a-device rail's DEVICE control (PROD-2193).
 *
 * Andrew, 2026-09-13: "the device dropdown should also be searchable". It was
 * a native `<select>` reading "Choose a device…", and this practice has 57
 * devices: no search, and a 57-row native listbox that a receptionist has to
 * scroll blind. The practitioner rail had already answered the same problem
 * with search over cards, so the device rail wears the same control — the
 * sibling adapter to `ProviderPickerGrid` over the shared `PickerGrid`,
 * standing in the same slot in the rail.
 *
 * The card says what actually distinguishes one machine from another:
 *   name        — the device.
 *   technician  — `technicianLine`, the SAME rule the Devices strip uses
 *                 (PROD-2184), so an unassigned machine reads
 *                 "No technician assigned" in both places rather than two
 *                 different ways.
 *
 * The card carried the device's `subtype` between those two until 2026-09-14
 * ("tier info please remove it from all the screens if still exists") — see
 * `deviceCardItem`. The SEARCH still matches on subtype, deliberately: that
 * costs nothing, keeps working for anyone who knows the catalogue term, and
 * it is only the label that had to stop advertising it.
 *
 * The list is NOT clamped behind a chevron the way the provider grid is:
 * 4-of-57 plus a "show more" that dumps the other 53 in one go is the native
 * `<select>`'s problem again. Every match renders, inside a bounded scroller,
 * and the search box is what narrows 57 to the handful you meant.
 *
 * Search state is owned HERE rather than lifted to the panel: the query is
 * pure view state, it is meaningless once a device is chosen (the grid is
 * replaced by the selected card) and the rail has no other use for it.
 */

/** Cards rendered before the area scrolls, rather than a "show more" chevron. */
const NO_CLAMP = Number.POSITIVE_INFINITY;

interface DevicePickerGridProps {
  /**
   * Every device the rail may book — ordered AND narrowed by the caller.
   * Since 2026-09-13 that narrowing is `isBookableDevice`: the picker offers
   * only machines with working hours and at least one product, because the
   * rest cannot produce a booking request however far the user gets.
   */
  devices: readonly CalendarDevice[];
  /** The chosen device, or null while the grid is still showing. */
  selected: CalendarDevice | null;
  onSelect: (device: CalendarDevice) => void;
  onClear: () => void;
  isLoading?: boolean;
  /**
   * View-only mode — hides the × on the selected card and the grid behind it.
   * The exact counterpart of `ProviderPickerGrid.readOnly`, added for the
   * block panel's edit view (PROD-2454): an existing block's actor is not
   * something the panel lets you change, for a machine any more than for a
   * practitioner. Default false, so the Book-a-device rail is unaffected.
   */
  readOnly?: boolean;
  /**
   * Override the "no devices at all" placeholder. The default names what a
   * BOOKING needs; a caller whose predicate is weaker (the block panel only
   * needs working hours) has to be able to say so, or the empty state sends
   * the user to configure something that would not have helped.
   */
  emptyText?: string;
}

export function DevicePickerGrid({
  devices,
  selected,
  onSelect,
  onClear,
  isLoading = false,
  readOnly = false,
  emptyText = 'No bookable devices — a device needs working hours and at least one product under Settings › Devices › Schedule.',
}: DevicePickerGridProps) {
  const [query, setQuery] = useState('');
  const matches = filterDevicesByQuery(devices, query);

  return (
    <PickerGrid
      items={matches.map(deviceCardItem)}
      selected={selected ? deviceCardItem(selected) : null}
      isLoading={isLoading}
      onSelect={(_item, i) => {
        onSelect(matches[i]);
        setQuery('');
      }}
      onClear={() => {
        onClear();
        setQuery('');
      }}
      searchValue={query}
      onSearchChange={setQuery}
      // Names the two things the card actually SHOWS. `filterDevicesByQuery`
      // still matches subtype and room underneath — a label promises, it does
      // not bound the match.
      searchPlaceholder="Search devices by name or technician"
      collapsedCount={NO_CLAMP}
      listClassName="max-h-72 overflow-y-auto scrollbar-hide"
      // The rail is fed only BOOKABLE devices (`isBookableDevice`) since
      // 2026-09-13, so an empty grid most often means the practice HAS
      // machines and none of them is configured — "no devices in this
      // practice" would send the user looking for the wrong thing. Name the
      // missing configuration and where it lives. Overridable because the
      // block panel's predicate is weaker; see the prop.
      emptyText={emptyText}
      noMatchText="No devices match this search."
      clearLabel="Clear selected device"
      readOnly={readOnly}
    />
  );
}
